<template>
  <div>
    <v-row v-if="stats" dense>
      <v-col cols="12" class="pl-3 pb-0 mt-3">
        <v-card
          class="ml-stats-card d-flex flex-wrap align-center"
          elevation="0"
        >
          <div class="ml-stats-title font-weight-medium">
            {{ $t("medkitlogs.statistics.title") }}: <b>{{ stats.total }}</b>
          </div>

          <v-chip small class="ml-stats-chip" color="primary" outlined>
            {{ $t("medkitlogs.statistics.thisYear") }}
            <span class="ml-1">
              <b>{{ stats.thisYear }}</b>
            </span>
          </v-chip>

          <v-chip small class="ml-stats-chip" color="primary" outlined>
            {{ $t("medkitlogs.statistics.thisMonth") }}
            <span class="ml-1">
              <b>{{ stats.thisMonth }}</b>
            </span>
          </v-chip>
        </v-card>
      </v-col>
    </v-row>

    <tableComponent
      ref="tableComponent"
      :tableSettings="tableSettings"
      :tableData="tableData"
      :headers="headers"
      @newDocument="newDocument"
      @exportWithTimespan="exportWithTimespan"
      @openMedkitlogQrCodeDialog="openMedkitlogQrCodeDialog"
      @openQrCodePoster="openQrCodePoster"
      @openMultiUserDialog="openMultiUserDialog"
      @confirmBulkDelete="confirmBulkDelete"
    />
    <v-dialog v-model="bulkConfirm.open" max-width="440">
      <v-card>
        <v-card-title>{{ bulkConfirm.title }}</v-card-title>
        <v-card-text>{{ bulkConfirm.message }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="bulkConfirm.open = false"
            :disabled="bulkConfirm.loading"
          >
            {{ $t("general.cancel") }}
          </v-btn>
          <v-btn
            color="error"
            :loading="bulkConfirm.loading"
            @click="runBulkDelete"
          >
            {{ $t("medkitlogs.bulk.deleteButton") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <medkitlogReportExportForm
      ref="medkitlogReportExportForm"
      :data="medkitlogReportExportForm"
      @reloadData="reloadDataFunc"
    />
    <medkitlogQrCodeDialog
      :medkitlogQrCodeDialog="medkitlogQrCodeDialog"
      @printPoster="openQrCodePoster"
    />
    <multiUserDialog
      v-model="multiUserOpen"
      entityType="medkitLog"
      :availableItems="transferItems"
      itemText="name"
      itemValue="_id"
      :clients="transferClients"
      :currentClientId="currentClientId"
      @confirm="executeTransfer"
      entityLabel="multiUser.dialog.medkitLogs"
    />
  </div>
</template>
<script>
import { getUniqueId } from "../../components/api/api";
import { downloadQrPoster } from "../../utils/posterDownload";
import { isAnonymized } from "../../utils/access";
import tableComponent from "../../components/_utils/dataTableHelpers/tableComponent.vue";
import medkitlogReportExportForm from "./medkitlogReportExportForm.vue";
import medkitlogQrCodeDialog from "./medkitlogQrCodeDialog.vue";
import multiUserDialog from "../../components/_utils/dataTableHelpers/actions/multiUserDialog.vue";

export default {
  components: {
    medkitlogQrCodeDialog,
    tableComponent,
    medkitlogReportExportForm,
    multiUserDialog,
  },
  data: () => ({
    currentUserId: "",
    medkitlogQrCodeDialog: {
      open: false,
    },
    tableSettings: {
      title: "",
      showSelect: true,
      showHeaderToolbar: true,
      multipleSelectButtons: [
        {
          title: "medkitlogs.bulk.deleteButton",
          color: "error",
          action: "confirmBulkDelete",
          icon: "delete",
          iconDark: true,
          dark: true,
          outlined: true,
        },
      ],
      headerButtons: [
        {
          title: "multiUser.button",
          color: "primary",
          action: "openMultiUserDialog",
          icon: "mdi-swap-horizontal",
          iconDark: true,
          dark: true,
        },
        {
          title: "medkitlogs.generateQrCode",
          color: "primary",
          action: "openMedkitlogQrCodeDialog",
          icon: "qr_code",
          iconDark: true,
          dark: true,
          outlined: true,
        },
        {
          title: "medkitlogs.createExport",
          color: "grey",
          action: "exportWithTimespan",
          icon: "download",
          iconDark: true,
          dark: true,
          outlined: true,
        },
        {
          title: "medkitlogs.createMedkitlog",
          color: "secondary",
          action: "newDocument",
          icon: "add",
          iconDark: true,
          dark: true,
          outlined: true,
        },
      ],
    },
    bulkConfirm: {
      open: false,
      loading: false,
      title: "",
      message: "",
      ids: [],
    },
    tableData: {
      tableComponentSlots: [
        {
          id: getUniqueId(),
          slotName: "item.formattedMedkitlogDate",
          componentName: "formattedMedkitlogDate",
        },
        {
          id: getUniqueId(),
          slotName: "item.formattedFirstAid",
          componentName: "formattedFirstAid",
        },
        {
          id: getUniqueId(),
          slotName: "item.formattedMed",
          componentName: "formattedMed",
        },
        {
          id: getUniqueId(),
          slotName: "item.medkitlogFormattedCreatedAt",
          componentName: "medkitlogFormattedCreatedAt",
        },
        {
          id: getUniqueId(),
          slotName: "item.medkitlogProofStatus",
          componentName: "medkitlogProofStatus",
        },
        {
          id: getUniqueId(),
          slotName: "item.medkitlogAction",
          componentName: "medkitlogAction",
          config: { currentUserId: "" },
        },
      ],
      itemKey: "_id",
      uriPartPrepend: "medkitlog",
      uriPartAppend: "",
      populate: [],
      sort: "-createdAt",
      editFormPushName: "medkitlogForm",
    },
    medkitlogReportExportForm: {
      open: false,
      title: "medkitlogs.exportTitle",
      item: null,
      method: "export",
    },
    currentClientId: "",
    isMultiUser: false,
    multiUserOpen: false,
    transferClients: [],
    transferItems: [],
    stats: null,
  }),
  async created() {
    this.$store.dispatch("getUserInfo").then((user) => {
      this.currentUserId = user?._id || "";
      const slot = this.tableData.tableComponentSlots.find(
        (x) => x.componentName === "medkitlogAction"
      );
      if (slot) slot.config = { currentUserId: this.currentUserId };
    });
    const { year, month } = this.$route.params;
    if (year && month) {
      this.tableData.uriPartAppend = `&year=${year}&month=${month}`;
    }

    const user = await this.$store.dispatch("getUserInfo");
    this.currentClientId = user?.client?._id || "";
    this.isMultiUser = user?.role === "MultiUser";

    this.fetchStats();

    const logs = await this.$store.dispatch("getAllMedkitlogs");
    this.transferItems = logs.data;

    if (!this.isMultiUser) {
      this.tableSettings.headerButtons =
        this.tableSettings.headerButtons.filter(
          (btn) => btn.action !== "openMultiUserDialog"
        );
    }
  },
  computed: {
    headers() {
      const anonymized = isAnonymized("medkitlog");
      return [
        ...(anonymized
          ? []
          : [
              {
                text: this.$t("medkitlogs.tableHeader.medKitLog"),
                value: "name",
                align: "left",
              },
            ]),
        {
          text: this.$t("medkitlogs.tableHeader.medKitLogDate"),
          value: "formattedMedkitlogDate",
        },
        {
          text: this.$t("medkitlogs.tableHeader.firstResponderNeeded"),
          value: "formattedFirstAid",
        },
        {
          text: this.$t("medkitlogs.tableHeader.medKitUsed"),
          value: "formattedMed",
        },
        {
          text: this.$t("medkitlogs.tableHeader.createdAt"),
          value: "medkitlogFormattedCreatedAt",
        },
        {
          text: this.$t("riskAssessment.tableHeader.proofed"),
          value: "medkitlogProofStatus",
          sortable: false,
        },
        {
          text: "",
          value: "medkitlogAction",
          align: "right",
          sortable: false,
        },
      ];
    },
  },
  methods: {
    reloadDataFunc() {
      this.$refs.tableComponent.getData();
      this.fetchStats();
    },
    confirmBulkDelete(selectedItems) {
      const ids = (selectedItems || []).map((i) => i._id);
      this.bulkConfirm = {
        open: true,
        loading: false,
        title: this.$t("medkitlogs.bulk.deleteTitle"),
        message: this.$t("medkitlogs.bulk.deleteConfirm", {
          count: ids.length,
        }),
        ids,
      };
    },
    async runBulkDelete() {
      this.bulkConfirm.loading = true;
      try {
        await this.$store.dispatch(
          "bulkDeleteMedkitlogs",
          this.bulkConfirm.ids
        );
        this.bulkConfirm.open = false;
        if (this.$refs.tableComponent) {
          this.$refs.tableComponent.clearSelectItems();
        }
        this.reloadDataFunc();
      } finally {
        this.bulkConfirm.loading = false;
      }
    },
    async fetchStats() {
      try {
        this.stats = await this.$store.dispatch("getMedkitlogStats");
      } catch (e) {
        this.stats = null;
      }
    },
    newDocument() {
      const { year, month } = this.$route.params;
      this.$router.push({
        name: this.tableData.editFormPushName,
        params: { id: "1", mode: "new", data: 1 },
        query: year && month ? { year, month } : {},
      });
    },
    exportWithTimespan() {
      this.medkitlogReportExportForm.open = true;
    },
    deleteItem(item) {
      //trigger multiple select delete
      this.$store.dispatch("deleteMultipleMedkitlog", item).then(() => {
        this.reloadDataFunc();
        this.multipleDeleteDialog.open = false;
        this.multipleDeleteDialog.items = [];
      });
    },
    openMedkitlogQrCodeDialog() {
      this.medkitlogQrCodeDialog.open = true;
    },
    async openQrCodePoster() {
      try {
        const settings = await this.$store.dispatch(
          "getSettingData",
          "qrCodeKeys"
        );
        let incidentKey = settings.value.incidentReport;
        let medkitKey = settings.value.medkitlog;

        if (!incidentKey) {
          const t = await this.$store.dispatch("newIncidentReportQrCode");
          incidentKey = t.token;
        }
        if (!medkitKey) {
          const t = await this.$store.dispatch("newMedkitlogQrCode");
          medkitKey = t.token;
        }

        await downloadQrPoster(incidentKey, medkitKey);
      } catch (e) {
        console.error(e);
      }
    },

    async openMultiUserDialog() {
      try {
        const res = await this.$store.dispatch("getTransferClients");
        this.transferClients = res.data || [];
      } catch (e) {
        // handle error however you prefer
        return;
      }
      this.multiUserOpen = true;
    },

    async executeTransfer(payload) {
      const parts = [];
      try {
        const result = await this.$store.dispatch("transferEntities", payload);

        if (result.totalSuccess > 0) {
          parts.push(
            `${result.totalSuccess} ${this.$t("multiUser.result.success")}`
          );
        }
        if (result.totalConflict > 0) {
          parts.push(
            `${result.totalConflict} ${this.$t("multiUser.result.errors")}`
          );
        }
        if (result.totalError > 0) {
          parts.push(
            `${result.totalError} ${this.$t("multiUser.result.errors")}`
          );
        }

        this.$store.commit("CREATE_SNACKBAR", parts.join(" · "));

        if (payload.action === "move" && result.totalSuccess > 0) {
          this.reloadDataFunc();
        }
      } catch (e) {
        this.$store.commit("triggerErrorSnackbar", parts.join(" · "));
      }
    },
  },
};
</script>

<style>
.ml-stats-card {
  padding: 4px 8px;
  gap: 6px;
  font-size: 0.875rem;
  line-height: 1.2;
}

.ml-stats-title {
  margin-right: 8px;
  white-space: nowrap;
}

.ml-stats-chip {
  height: 22px;
  font-size: 0.75rem;
}
</style>
